import React, { useEffect, useRef, useState } from 'react';
import { OutageEvent, Scalars } from '../../../../__generated__/pge-types';
import { OutageLngLat } from '../../types';
import Supercluster, { ClusterFeature, PointFeature } from 'supercluster';
import { getImpactedCustomersLabel, isValidCoordinate } from '../../utils/map';
import { CONSTANTS } from '../../constants';
import { Marker } from 'react-map-gl';
import { useTranslation } from '../../../../hooks/useTranslation';
import { makeStyles } from '@material-ui/core';
import OutageMarkerIcon from '../icons/OutageMarkerIcon';
import useAuth from '../../../../hooks/useAuth';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import mainColors from '../../../../themes/main-colors';
import useGetOutageEventDetails from '../../hooks/useGetOutageEventDetails';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import { useOutageMapContext } from '../../providers/OutageMapProvider';
import { useErrorNotification } from '../common/error-notification';

export type PointProperties = {
  /** index back to `data` array to recover the original outage */
  outageIndex: number;
  totalCustomersImpacted: number;
};

export type ClusterBaseProps = {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: number;
};

export interface ClusterProperties {
  impactedCustomers: number;
}

const useStyles = makeStyles(() => ({
  cursorPointer: {
    cursor: 'pointer',
  },
  icon: {
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    transition: '0.2s',
    '&:hover': {
      width: '40px',
      height: '40px',
      zIndex: 4,
    },
  },
  iconSlected: {
    width: '40px',
    height: '40px',
    cursor: 'pointer',
  },
}));

const getMarkerIcon = (
  title: string,
  impactedCustomers: number,
  isSelected: boolean = false,
  shape: 'square' | 'round' = 'square',
  className: string,
) => {
  return (
    <OutageMarkerIcon
      text={getImpactedCustomersLabel(impactedCustomers)}
      title={title}
      variant={shape}
      className={className}
      size={isSelected ? 32 : 40}
      fillColor={isSelected ? mainColors.white : mainColors.roseRed}
      strokeColor={isSelected ? mainColors.roseRed : mainColors.white}
      textColor={isSelected ? mainColors.roseRed : mainColors.white}
    />
  );
};

export type OutagesClusterOverlayProp = {
  outages: OutageEvent[];
};

const OutagesCluster: React.FC<OutagesClusterOverlayProp> = ({ outages }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const outageFitBoundsCalled = useRef(false);
  const { isAuthenticated } = useAuth();
  const {
    setOutageMarkerView,
    outageMarkerView,
    setSelectedOutageInfo,
  } = useOutageLeftPanelState();
  const { setPage } = useOutageResponsivePageTracker();
  const { getOutageEventDetails } = useGetOutageEventDetails();
  const { setErrorNotification } = useErrorNotification();
  const { wrapWithLoader } = useWrapWithLoader();
  const { fitBounds, isLoaded, mapInstance: map } = useOutageMapContext();

  const outagesData = outages.filter(m =>
    isValidCoordinate(parseFloat(m.lat), parseFloat(m.long)),
  );
  const [
    selectedOutageEvent,
    setSelectedOutageEvent,
  ] = useState<OutageEvent | null>();

  useEffect(() => {
    if (
      outages &&
      outages.length > 0 &&
      isLoaded &&
      !outageFitBoundsCalled.current &&
      !isAuthenticated
    ) {
      const p: OutageLngLat[] = [];

      outages.map(outage =>
        p.push({
          lat: parseFloat(outage.lat),
          lng: parseFloat(outage.long),
        }),
      );

      // Fit the map to the calculated bounds with some padding
      fitBounds(p);
      outageFitBoundsCalled.current = true;
    }
  }, [outages, isLoaded, fitBounds]);

  //Zoom into the selected outage event
  useEffect(() => {
    if (selectedOutageEvent && map) {
      map?.flyTo({
        center: [selectedOutageEvent.long, selectedOutageEvent.lat],
        zoom: 15,
        duration: 0, // ms
        essential: true, // respects prefers-reduced-motion
      });
    }
  }, [selectedOutageEvent, map]);

  useEffect(() => {
    if (!outageMarkerView) {
      setSelectedOutageEvent(null);
    }
  }, [outageMarkerView]);

  const onSelectOutageEvent = wrapWithLoader(async (outage: OutageEvent) => {
    setSelectedOutageEvent(outage);

    //For mobile responsive, invoke the details panel
    setPage(ResponsivePageView.Details);

    try {
      const response = await getOutageEventDetails({
        outageEventId: outage.eventId,
      });
      if (response) {
        setSelectedOutageInfo(response);
        setOutageMarkerView(OutageMarkerView.CustomerAffected);
      } else {
        setOutageMarkerView(OutageMarkerView.NoOutage);
      }
    } catch (e) {
      setErrorNotification();
    }
  });

  // Convert outages to GeoJSON points
  const points = React.useMemo<PointFeature<PointProperties>[]>(() => {
    return outagesData
      .map((item, i) => {
        const { lat, long, totalCustomersImpacted } = item;

        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [long, lat] as [number, number],
          },
          properties: {
            outageIndex: i,
            totalCustomersImpacted: Number(totalCustomersImpacted) || 0,
          },
        };
      })
      .filter(Boolean) as PointFeature<PointProperties>[];
  }, [outagesData]);

  //Build supercluster with custom aggregation for impactedCustomers
  const index = React.useMemo(() => {
    const sc = new Supercluster<PointProperties, ClusterProperties>({
      radius: CONSTANTS.MAP_CONFIG.maxClusterRadius,
      maxZoom: CONSTANTS.MAP_CONFIG.maxClusterZoom,
      // aggregate sumCustomers per cluster
      map: props => ({ impactedCustomers: props.totalCustomersImpacted ?? 0 }),
      reduce: (accumulated, props) => {
        accumulated.impactedCustomers += props.impactedCustomers;
      },
    });
    // Supercluster expects GeoJSON features
    sc.load(points);
    return sc;
  }, [points]);

  const [bbox, setBbox] = React.useState<
    [number, number, number, number] | null
  >(null);
  const [zoom, setZoom] = React.useState<number>(0);

  //Keep bounds & zoom from the map to request clusters
  React.useEffect(() => {
    if (!map) {
      return;
    }

    const update = () => {
      const b = map.getBounds();
      if (!b) {
        return;
      }
      setBbox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
      setZoom(map.getZoom());
    };

    update();
    //const evt = recalcOn === 'move' ? 'move' : 'moveend';
    const evt = 'move';
    map.on(evt, update);
    return () => {
      if (map) {
        map.off(evt, update);
      }
    };
  }, [map]);

  // 4) Compute visible clusters/features
  const clusters = React.useMemo(() => {
    if (!bbox) {
      return [] as Array<
        | PointFeature<PointProperties>
        | ClusterFeature<ClusterBaseProps & ClusterProperties>
      >;
    }
    const z = Math.round(zoom);
    return index.getClusters(bbox, z) as Array<
      | PointFeature<PointProperties>
      | ClusterFeature<ClusterBaseProps & ClusterProperties>
    >;
  }, [index, bbox, zoom]);

  return (
    <>
      {clusters.map(feature => {
        const [lng, lat] = feature.geometry.coordinates as [number, number];
        const props = feature.properties as ClusterBaseProps &
          ClusterProperties;
        const { cluster_id, impactedCustomers } = props;

        const title =
          impactedCustomers > 1 ? t('REPORTED_OUTAGES') : t('REPORTED_OUTAGE');

        // Cluster feature (has aggregated props)
        if ((feature.properties as ClusterBaseProps).cluster) {
          const onClickCluster = () => {
            const expZoom = Math.min(
              index.getClusterExpansionZoom(cluster_id),
              CONSTANTS.MAP_CONFIG.maxClusterZoom + 2,
            );

            map?.easeTo({
              center: [lng, lat],
              zoom: expZoom,
              duration: 500,
            });
          };

          const markerClusterProps = {
            key: cluster_id,
            title,
            impactedCustomers,
            lat,
            long: lng,
            className: classes.icon,
            onClick: onClickCluster,
          };

          return <OutageClusterMarker {...markerClusterProps} />;
        }

        // Unclustered point -> render outage marker
        const outageIndex = (feature.properties as PointProperties).outageIndex;
        const outage = outagesData[outageIndex];

        const isSelectedOutage =
          outage &&
          selectedOutageEvent?.eventId === outage.eventId &&
          (outageMarkerView === OutageMarkerView.CustomerAffected ||
            outageMarkerView === OutageMarkerView.Grouped);

        const markerProps = {
          key: outageIndex,
          title,
          outage,
          isSelectedOutage,
          className: isSelectedOutage ? classes.iconSlected : classes.icon,
          onClick: onSelectOutageEvent,
        };

        return <OutageMarker {...markerProps} />;
      })}
    </>
  );
};

interface OutageClusterMarkerProps {
  key: number;
  title: string;
  impactedCustomers: number;
  className: string;
  lat: Scalars['Latitude'];
  long: Scalars['Longitude'];
  onClick: () => void;
}

const OutageClusterMarker = React.memo(
  ({
    key,
    title,
    impactedCustomers,
    className,
    lat,
    long,
    onClick,
  }: OutageClusterMarkerProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Marker
        key={`cluster-${key}`}
        longitude={long}
        latitude={lat}
        anchor="center"
        style={{ zIndex: isHovered ? 5 : 2 }}
        onClick={onClick}
      >
        <div
          onClick={e => {
            e.preventDefault(); //Prevents triggering the PSPS polygon onclick handler
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {getMarkerIcon(
            title,
            impactedCustomers,
            undefined,
            undefined,
            className,
          )}
        </div>
      </Marker>
    );
  },
);

interface OutageMarkerProps {
  key: number;
  title: string;
  isSelectedOutage: boolean;
  outage: OutageEvent;
  className: string;
  onClick: (outage: OutageEvent) => void;
}

const OutageMarker = React.memo(
  ({
    key,
    title,
    isSelectedOutage,
    outage,
    className,
    onClick,
  }: OutageMarkerProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Marker
        key={`outage-${key}`}
        longitude={outage.long}
        latitude={outage.lat}
        anchor="center"
        style={{ zIndex: isSelectedOutage || isHovered ? 5 : 2 }}
        onClick={e => {
          if (outage) {
            void onClick(outage);
          }
        }}
      >
        <div
          onClick={e => {
            e.preventDefault(); //Prevents triggering the PSPS polygon onclick handler
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {getMarkerIcon(
            title,
            outage?.totalCustomersImpacted,
            isSelectedOutage,
            'round',
            className,
          )}
        </div>
      </Marker>
    );
  },
);

export const OutagesClusterOverlay = React.memo(OutagesCluster);
